import React from 'react';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  CloudHail, 
  Car, 
  CheckCircle, 
  ShieldCheck, 
  Zap, 
  Euro, 
  MapPin, 
  Clock,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { services, companyInfo } from '@/data';
import { ROUTE_PATHS, cn } from '@/lib';
import { ServiceCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';

export default function Services() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="flex flex-col gap-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-secondary/30 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img 
            src={IMAGES.TOOLS_WORKSHOP_5} 
            alt="Workshop Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Unsere <span className="text-primary">Leistungen</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Professionelle Dellenentfernung ohne Lackieren (PDR). Wir erhalten den Wert 
              Ihres Fahrzeugs mit höchster Präzision und modernster Technik.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full">
                <Link to={ROUTE_PATHS.CONTACT}>Termin vereinbaren</Link>
              </Button>
              <a href="#pricing">
                <Button variant="outline" size="lg" className="rounded-full">
                  Preisübersicht
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Was wir für Sie tun können</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Ob kleiner Parkrempler oder schwerer Hagelschaden – wir haben die passende Lösung 
            für Ihr Fahrzeug.
          </p>
        </div>
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8"
        >
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </motion.div>
      </section>

      {/* Process Section - How it works */}
      <section className="bg-primary text-primary-foreground py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6">Das PDR-Verfahren: Sanfte Instandsetzung</h2>
              <p className="text-primary-foreground/80 mb-8 leading-relaxed">
                PDR steht für "Paintless Dent Repair" – auf Deutsch: Ausbeulen ohne Lackieren. 
                Dabei wird die Delle von der Rückseite mit speziellen Werkzeugen präzise 
                herausgedrückt oder von der Außenseite sanft gezogen. 
              </p>
              <ul className="space-y-4">
                {[
                  'Erhalt des originalen Fahrzeuglacks',
                  'Keine Spachtelarbeiten oder Farbunterschiede',
                  'Deutliche Zeit- und Kostenersparnis gegenüber herkömmlichen Methoden',
                  'Umweltfreundlich, da keine Lacke oder Lösungsmittel benötigt werden'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-chart-4 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <img 
                src={IMAGES.MECHANIC_WORK_1} 
                alt="Experte bei der Arbeit" 
                className="w-full aspect-video object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-white font-medium">Präzision in jedem Handgriff</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="container mx-auto px-4 py-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Transparente Preise</h2>
          <p className="text-muted-foreground">Fair, kalkulierbar und deutlich günstiger als Neulackierung.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: 'Parkdelle Standard',
              price: 'ab 60 €',
              desc: 'Kleine Dellen bis ca. 2cm (Euro-Münze)',
              features: ['Reparatur in ca. 30-60 Min', 'Vor-Ort Service möglich', 'Perfektes Finish']
            },
            {
              title: 'Große Delle / Knick',
              price: 'ab 120 €',
              desc: 'Dellen über 5cm oder mit scharfen Kanten',
              features: ['Aufwändigere Drucktechnik', 'Inkl. Politur der Stelle', 'Kein Lackieren nötig']
            },
            {
              title: 'Leasing-Paket',
              price: 'ab 180 €',
              desc: 'Komplett-Check & Beseitigung aller Mängel',
              features: ['Vorab-Begutachtung', 'Beseitigung mehrerer Dellen', 'Vermeidung von Nachzahlungen']
            }
          ].map((plan, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-card border border-border p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <h3 className="text-xl font-bold mb-2">{plan.title}</h3>
              <div className="text-3xl font-bold text-primary mb-4">{plan.price}</div>
              <p className="text-sm text-muted-foreground mb-6">{plan.desc}</p>
              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feat, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm">
                    <Zap className="h-4 w-4 text-primary" />
                    {feat}
                  </li>
                ))}
              </ul>
              <Button variant={i === 1 ? 'default' : 'outline'} className="w-full rounded-full">
                Anfragen
              </Button>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>* Alle Preise sind Richtwerte und hängen von Lage, Tiefe und Zugänglichkeit der Delle ab. Hagelschäden werden individuell nach Gutachten berechnet.</p>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="bg-secondary/20 py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <MapPin className="h-4 w-4" />
                <span>Einsatzgebiet</span>
              </div>
              <h2 className="text-3xl font-bold mb-6">Wir sind für Sie mobil</h2>
              <p className="text-muted-foreground mb-8">
                Wir bieten professionellen mobilen Service in {companyInfo.serviceArea} für 
                Autohäuser, Fuhrparks und Privatkunden direkt vor Ort.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'München (alle Stadtteile)',
                  'Dachau',
                  'Fürstenfeldbruck',
                  'Starnberg',
                  'Freising',
                  'Erding',
                  'Ebersberg',
                  'Wolfratshausen'
                ].map((city) => (
                  <div key={city} className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    <span className="text-sm font-medium">{city}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="bg-card p-8 rounded-2xl border border-border shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold">Schnelle Termine</h4>
                    <p className="text-sm text-muted-foreground">Meist innerhalb von 48 Stunden</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold">Zertifizierte Qualität</h4>
                    <p className="text-sm text-muted-foreground">Meistergeführter Fachbetrieb</p>
                  </div>
                </div>
                <Button asChild className="w-full rounded-full py-6 text-lg">
                  <Link to={ROUTE_PATHS.CONTACT}>
                    Jetzt Termin anfragen <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
